import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, X, ArrowRight, Trophy } from 'lucide-react';
import { recordGuestFunnelEvent } from '../lib/guestFunnel';
import '../styles/GuestSaveSpotNudge.css';

const SESSION_KEY_50 = 'cadence_guest_savespot_dismissed';
const SESSION_KEY_90 = 'cadence_guest_savespot_dismissed_90';
const TRIGGER_50 = 50;
const TRIGGER_90 = 90;

const VARIANTS = {
  halfway: {
    icon: Bookmark,
    title: "You're halfway home.",
    lede: "You've put real care into this. Save your spot now \u2014 here's why it matters:",
    bullets: [
      'Your progress follows you to any device',
      'Never starts over',
      'Secure report delivery when we\u2019re done',
      'We\u2019ll need your email at submission anyway',
    ],
    aside: 'Prefer to keep going? Tap \u00d7 and your work stays right here.',
    primaryCta: 'Save My Spot',
    sessionKey: SESSION_KEY_50,
    triggerAt: TRIGGER_50,
  },
  finishLine: {
    icon: Trophy,
    title: "You're at the finish line.",
    lede: "Don't let this moment slip away. Lock in your work before you submit:",
    bullets: [
      'Receive your report securely in the portal',
      'Reply to the team with one tap',
      'Pull up everything later from any device',
      'We need your email at submission anyway',
    ],
    aside: 'Already have an account? Tap \u00d7 and sign in after you submit.',
    primaryCta: 'Secure My Work',
    sessionKey: SESSION_KEY_90,
    triggerAt: TRIGGER_90,
  },
};

/**
 * Two-tier dismissible nudge for guest visitors:
 *  - 50% threshold -> "halfway home" variant (existing).
 *  - 90% threshold -> "finish line" variant (new, last natural conversion
 *    moment before submit). Different copy so it doesn't feel repetitive.
 *
 * Each variant is one-shot per session via separate sessionStorage keys.
 * Logged-in or non-guest users never see either.
 */
const GuestSaveSpotNudge = ({ progressPercent, isGuest, isAuthenticated }) => {
  const navigate = useNavigate();
  const [activeVariant, setActiveVariant] = useState(null);

  useEffect(() => {
    if (isAuthenticated || !isGuest) {
      setActiveVariant(null);
      return;
    }
    // 90% finish-line variant takes precedence — only re-checks when the
    // higher threshold is crossed.
    if (
      progressPercent >= TRIGGER_90 &&
      sessionStorage.getItem(SESSION_KEY_90) !== '1'
    ) {
      setActiveVariant('finishLine');
      recordGuestFunnelEvent('modal_90_shown', progressPercent);
      return;
    }
    if (
      progressPercent >= TRIGGER_50 &&
      progressPercent < TRIGGER_90 &&
      sessionStorage.getItem(SESSION_KEY_50) !== '1'
    ) {
      setActiveVariant('halfway');
      recordGuestFunnelEvent('modal_50_shown', progressPercent);
    }
  }, [progressPercent, isGuest, isAuthenticated]);

  if (!activeVariant) return null;
  const v = VARIANTS[activeVariant];
  const Icon = v.icon;

  const dismiss = () => {
    sessionStorage.setItem(v.sessionKey, '1');
    recordGuestFunnelEvent(
      activeVariant === 'finishLine' ? 'modal_90_dismissed' : 'modal_50_dismissed',
      progressPercent
    );
    setActiveVariant(null);
  };

  const handleSaveSpot = () => {
    sessionStorage.setItem(v.sessionKey, '1');
    recordGuestFunnelEvent(
      activeVariant === 'finishLine' ? 'modal_90_saved' : 'modal_50_saved',
      progressPercent
    );
    navigate('/login?redirect=intake&mode=signup');
  };

  return (
    <div
      className={`savespot-overlay savespot-variant-${activeVariant}`}
      role="dialog"
      aria-modal="true"
      data-testid="guest-savespot-nudge"
      data-variant={activeVariant}
    >
      <div className="savespot-card">
        <button
          type="button"
          className="savespot-close"
          onClick={dismiss}
          aria-label="Continue as guest"
          data-testid="guest-savespot-close"
        >
          <X size={18} />
        </button>

        <div className="savespot-icon">
          <Icon size={24} />
        </div>

        <h3 className="savespot-title">{v.title}</h3>

        <p className="savespot-lede">{v.lede}</p>

        <ul className="savespot-bullets">
          {v.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>

        <p className="savespot-aside">{v.aside}</p>

        <div className="savespot-actions">
          <button
            type="button"
            className="savespot-primary"
            onClick={handleSaveSpot}
            data-testid="guest-savespot-save"
          >
            {v.primaryCta} <ArrowRight size={16} />
          </button>
          <button
            type="button"
            className="savespot-ghost"
            onClick={dismiss}
            data-testid="guest-savespot-continue-guest"
          >
            Continue as guest
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestSaveSpotNudge;
